import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn, displayName } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useHostCandidates } from "@/hooks/useHostCandidates";

interface CoHostSelectProps {
  value?: string;
  primaryHostId?: string;
  onChange: (hostId: string) => void;
}

/**
 * Optional second host for events run with two groups (e.g. double gallery strolls).
 * Only real host/artist profiles can be co-hosts — no "Nimi"/"Museum" placeholders.
 */
const CoHostSelect = ({ value, primaryHostId, onChange }: CoHostSelectProps) => {
  const [open, setOpen] = useState(false);
  const { data: hosts, isLoading } = useHostCandidates([value, primaryHostId]);

  const hostLabel = (host: { first_name: string | null; last_name: string | null; member_type: string | null }) =>
    `${displayName(host.first_name ?? undefined, host.last_name ?? undefined)}${
      host.member_type === "artist" ? " (Artist)" : ""
    }`;

  const options = (hosts ?? []).filter((h) => h.id !== primaryHostId);
  const selectedHost = options.find((h) => h.id === value);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label htmlFor="secondaryHostId">Second host (optional)</Label>
        {value && (
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange("")}>
            Clear
          </Button>
        )}
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id="secondaryHostId"
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between bg-card border-input font-normal"
          >
            <span className={cn(!selectedHost && "text-muted-foreground")}>
              {selectedHost ? hostLabel(selectedHost) : isLoading && value ? "Loading..." : "No second host"}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-popover" align="start">
          <Command>
            <CommandInput placeholder="Type host name to search..." />
            <CommandList>
              <CommandEmpty>{isLoading ? "Loading hosts..." : "No host found."}</CommandEmpty>
              <CommandGroup heading="Hosts">
                {options.map((host) => (
                  <CommandItem
                    key={host.id}
                    value={`${hostLabel(host)} ${host.id}`}
                    onSelect={() => {
                      onChange(host.id);
                      setOpen(false);
                    }}
                  >
                    <Check className={cn("mr-2 h-4 w-4", value === host.id ? "opacity-100" : "opacity-0")} />
                    {hostLabel(host)}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <p className="text-xs text-muted-foreground">
        Add a co-host when the event runs as two groups. Both hosts get check-in access.
      </p>
    </div>
  );
};

export default CoHostSelect;
